import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import BreadCrumbs from '../components/BreadCrumbs';
import { setCartCount } from '../store/reducers/auth';
import { cartsData, isGuestUser } from '../utils/constants';
import { removeFromLocalStorage } from '../utils/localStorageUtils';

const CompleteOrder = () => {
    const isLoggedIn = useSelector(state => state.auth.isLoggedIn);
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const orderRef = searchParams.get("order_id") || location.state?.order_id || "N/A";
    const success = searchParams.get("success") || location.state?.success;
    const orderDate = searchParams.get("created_at") || location.state?.created_at;
    let orderMessage = searchParams.get("message") || location.state?.message;
    const dispatch = useDispatch();

    if(!orderMessage)
    {
        orderMessage = success
        ? 'Thank you! Your order has been placed successfully. We\'ll notify you once it\'s being processed.'
        : 'Your payment was unsuccessful, so your order could not be completed.';
    }

    const currentDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    if (success) {
        removeFromLocalStorage(isGuestUser);
        removeFromLocalStorage(cartsData);
        dispatch(setCartCount(0));
    }

    return (
        <div className="static-page-wrapper">
            <BreadCrumbs title1={'Home'} title2={'Order'} title3={'Complete'} />
            
            <style>
                {`
                .premium-thankyou-bg {
                    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
                    min-height: 70vh;
                    display: flex;
                    align-items: center;
                    padding: 3rem 0;
                }
                .premium-card {
                    background: rgba(255, 255, 255, 0.95);
                    backdrop-filter: blur(20px);
                    border: 1px solid rgba(255, 255, 255, 0.8);
                    border-radius: 28px;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.08), 0 10px 15px -3px rgba(0, 0, 0, 0.04);
                    overflow: hidden;
                    position: relative;
                }
                .premium-card::before {
                    content: '';
                    position: absolute;
                    top: 0; left: 0; right: 0;
                    height: 8px;
                    background: ${success ? 'linear-gradient(90deg, #10b981, #34d399)' : 'linear-gradient(90deg, #ef4444, #f87171)'};
                }
                .icon-wrapper {
                    width: 110px;
                    height: 110px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto;
                    position: relative;
                }
                .icon-wrapper.success {
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                    color: white;
                    box-shadow: 0 12px 25px -5px rgba(16, 185, 129, 0.4);
                }
                .icon-wrapper.failed {
                    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
                    color: white;
                    box-shadow: 0 12px 25px -5px rgba(239, 68, 68, 0.4);
                }
                .icon-pulse {
                    position: absolute;
                    top: 0; left: 0; right: 0; bottom: 0;
                    border-radius: 50%;
                    animation: pulse-ring 2.5s infinite cubic-bezier(0.215, 0.61, 0.355, 1);
                    z-index: -1;
                }
                .success .icon-pulse { border: 3px solid rgba(16, 185, 129, 0.5); }
                .failed .icon-pulse { border: 3px solid rgba(239, 68, 68, 0.5); }
                @keyframes pulse-ring {
                    0% { transform: scale(1); opacity: 0.8; }
                    100% { transform: scale(1.6); opacity: 0; }
                }
                .premium-btn-primary {
                    background: linear-gradient(135deg, #2563eb 0%, #4f46e5 100%);
                    color: white !important;
                    border: none;
                    border-radius: 14px;
                    font-weight: 600;
                    transition: all 0.3s ease;
                    box-shadow: 0 6px 15px 0 rgba(79, 70, 229, 0.3);
                    text-decoration: none;
                }
                .premium-btn-primary:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 8px 25px rgba(79, 70, 229, 0.4);
                }
                .premium-btn-secondary {
                    background: white;
                    color: #334155 !important;
                    border: 2px solid #e2e8f0;
                    border-radius: 14px;
                    font-weight: 600;
                    transition: all 0.3s ease;
                    text-decoration: none;
                }
                .premium-btn-secondary:hover {
                    background: #f8fafc;
                    border-color: #cbd5e1;
                    color: #0f172a !important;
                    transform: translateY(-3px);
                    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
                }
                .premium-info-box {
                    background: #f8fafc;
                    border-radius: 20px;
                    border: 1px dashed #cbd5e1;
                }
                `}
            </style>

            <section className="premium-thankyou-bg">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-10 col-lg-7 col-xl-6">
                            <div className="premium-card p-5 text-center animate__animated animate__fadeInUp">
                                
                                {/* Status Icon */}
                                <div className="mb-4">
                                    <div className={`icon-wrapper ${success ? 'success' : 'failed'} animate__animated animate__zoomIn animate__delay-1s`}>
                                        <div className="icon-pulse"></div>
                                        <i className={`lni ${success ? 'lni-checkmark' : 'lni-cross-circle'}`} style={{ fontSize: '4rem' }}></i>
                                    </div>
                                </div>

                                {/* Order Status Message */}
                                <div className="mb-5 animate__animated animate__fadeInUp animate__delay-1s">
                                    <h1 className="fw-bolder mb-3 text-dark" style={{ fontSize: '2.8rem', letterSpacing: '-1.5px' }}>
                                        {success ? 'Thank You!' : 'Order Failed'}
                                    </h1>
                                    <p className="text-secondary px-3" style={{ fontSize: '1.15rem', lineHeight: '1.7' }}>
                                        {orderMessage}
                                    </p>
                                </div>

                                {/* Order Info Box */}
                                <div className="premium-info-box p-4 mb-5 animate__animated animate__fadeInUp animate__delay-2s">
                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mb-3 pb-3 border-bottom">
                                        <div className="text-secondary fw-semibold text-uppercase" style={{ fontSize: '0.8rem', letterSpacing: '1.5px' }}>Order Reference</div>
                                        <div className="fw-bold text-dark text-break text-center text-sm-end fs-5">{orderRef}</div>
                                    </div>
                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center">
                                        <div className="text-secondary fw-semibold text-uppercase" style={{ fontSize: '0.8rem', letterSpacing: '1.5px' }}>Order Date</div>
                                        <div className="fw-medium text-dark text-center text-sm-end">{orderDate ?? currentDate}</div>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="d-flex flex-column flex-sm-row justify-content-center gap-3 animate__animated animate__fadeInUp animate__delay-3s">
                                    {success ? (
                                        <>
                                            {isLoggedIn && (
                                                <Link to="/myorders" className="premium-btn-secondary d-flex justify-content-center align-items-center w-100" style={{ height: '56px', maxWidth: '220px', marginRight: '3px' }}>
                                                    <i className="lni lni-list fs-5" style={{ marginRight: '8px' }}></i> View Details
                                                </Link>
                                            )}
                                            <Link to="/" className="premium-btn-primary d-flex justify-content-center align-items-center w-100" style={{ height: '56px', maxWidth: '220px' }}>
                                                <i className="lni lni-shopping-basket fs-5" style={{ marginRight: '8px' }}></i> Continue Shopping
                                            </Link>
                                        </>
                                    ) : (
                                        <Link to="/cart" className="premium-btn-primary d-flex justify-content-center align-items-center w-100" style={{ height: '56px', maxWidth: '220px' }}>
                                            <i className="lni lni-reload fs-5" style={{ marginRight: '8px' }}></i> Try Again
                                        </Link>
                                    )}
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    )
}

export default CompleteOrder;